import { useEffect, useState } from 'react';
import { getItem, larvae } from '@fakerpg/core';
import { useLang, useSnapshot } from '../game/context.js';
import { ChaferArt, ChestArt, CurrencyIcon, DofusArt, ElementEmblem, ItemArt, LarvaArt, LeagueArt, MonsterArt, OrnamentArt } from './art.js';
import { discordEmojiFor, labelFor, type AssetKey } from './keys.js';
import { resolveArtwork, resolveEmoji, useMirrorStatus } from './mirror.js';

/**
 * Resolves an asset key through the active pack (ADR-009):
 *   glyph   → generated art only;
 *   discord → the bot's original artwork: character portraits and speaker /
 *             chapter art as full pictures, everything else as its emoji —
 *             from the local mirror when it exists, else from Discord.
 * Any image that fails to load falls back to the generated art, so the
 * layout never changes with the pack.
 */
export function Sprite({
  asset,
  size = 32,
  title,
  masked = false,
  element,
  boss = false,
  decorative = false,
  image,
}: {
  asset: AssetKey;
  size?: number;
  title?: string;
  masked?: boolean;
  /** Tint for generated monster art (emoji keys). */
  element?: string;
  boss?: boolean;
  decorative?: boolean;
  /** Original artwork URL (speaker avatar, chapter art). */
  image?: string;
}) {
  const pack = useSnapshot().prefs.assetPack;
  useMirrorStatus(); // re-render once the mirror manifest arrives
  const lang = useLang();
  const label = title ?? labelFor(asset, lang);

  const portrait = masked
    ? null
    : resolveArtwork(asset.kind === 'item' && asset.type === 'characters' ? getItem('characters', asset.id)?.image : image, pack);
  const emoji = masked ? null : discordEmojiFor(asset);
  const emojiSrc = emoji ? resolveEmoji(emoji.id, emoji.animated, pack) : null;
  const candidates = [portrait, emojiSrc].filter((c): c is string => !!c);
  const chain = candidates.join('|');

  const [failed, setFailed] = useState<string[]>([]);
  useEffect(() => setFailed([]), [chain]);
  const shown = candidates.find((c) => !failed.includes(c)) ?? null;
  const isPhoto = !!shown && shown === portrait;

  let art;
  switch (asset.kind) {
    case 'item':
      art = <ItemArt type={asset.type} id={asset.id} size={size} masked={masked} hideEmblem={!!shown} />;
      break;
    case 'emoji':
      art = shown ? null : <MonsterArt seed={asset.id || asset.name} element={element} size={size} boss={boss} />;
      break;
    case 'ui':
      art = shown ? null : <CurrencyIcon name={asset.name} size={size} />;
      break;
    case 'element':
      art = shown ? null : <ElementEmblem element={String(asset.element)} size={size} />;
      break;
    case 'chest':
      art = shown ? null : <ChestArt name={asset.name} size={size} />;
      break;
    case 'league':
      art = shown ? null : <LeagueArt rank={asset.rank} size={size} />;
      break;
    case 'chafer':
      art = shown ? null : <ChaferArt crit={asset.crit} size={size} />;
      break;
    case 'larva':
      art = shown ? null : <LarvaArt color={larvae[asset.index]?.color ?? '#8b6cff'} size={size} />;
      break;
    case 'ornament':
      art = shown ? null : <OrnamentArt seed={asset.seed} side={asset.side} size={size} />;
      break;
    case 'dofus':
      art = shown ? null : <DofusArt {...(asset.tier ? { tier: asset.tier } : {})} seed={asset.token} size={size} dim={!!asset.dim} />;
      break;
  }
  return (
    <span
      className={`sprite sprite--${asset.kind} ${isPhoto ? 'sprite--photo' : ''}`}
      style={{ width: size, height: size }}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label, title: label })}
    >
      {art}
      {shown && (
        <img
          className={isPhoto ? 'sprite__photo' : 'sprite__emoji'}
          src={shown}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailed((f) => [...f, shown])}
        />
      )}
    </span>
  );
}
